import * as THREE from 'three';

const loader = new THREE.TextureLoader();

function loadAlbedo(url: string, rx: number, ry: number): THREE.Texture {
  const tex = loader.load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function tinted(base: THREE.MeshStandardMaterial, color: number): THREE.MeshStandardMaterial {
  const mat = base.clone();
  mat.color.setHex(color);
  return mat;
}

function wrapCanvasTex(c: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Cream clinkers (running bond). Repeats as ~12–15 cm tiles at TILE_WALL. */
function makeClinkerTileMap(): THREE.CanvasTexture {
  const size = 512;
  const cols = 5;
  const rows = 10;
  const tw = size / cols;
  const th = size / rows;
  const grout = 4;
  const albedo = document.createElement('canvas');
  albedo.width = size;
  albedo.height = size;
  const actx = albedo.getContext('2d')!;
  actx.fillStyle = '#8a8680';
  actx.fillRect(0, 0, size, size);

  const shades = ['#f2eee4', '#ebe6da', '#f6f1e8', '#e4dfd2', '#efe9dc'];
  for (let row = 0; row < rows; row++) {
    const odd = row & 1;
    const y = row * th;
    for (let col = -1; col <= cols; col++) {
      const x = col * tw + (odd ? tw * 0.5 : 0);
      const shade = shades[((row * 17 + col * 13) >>> 0) % shades.length];
      actx.fillStyle = shade;
      actx.fillRect(x + grout * 0.5, y + grout * 0.5, tw - grout, th - grout);
    }
  }

  return wrapCanvasTex(albedo, true);
}

/** Institutional teal floor tiles — clean, not grimy metal. */
function makeTealFloorTileMap(): THREE.CanvasTexture {
  const size = 512;
  const n = 4;
  const t = size / n;
  const grout = 6;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#3a4a48';
  ctx.fillRect(0, 0, size, size);
  const shades = ['#4a8a82', '#3d7a74', '#528f88', '#457e78'];
  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      ctx.fillStyle = shades[(row * 5 + col * 3) % shades.length];
      ctx.fillRect(col * t + grout * 0.5, row * t + grout * 0.5, t - grout, t - grout);
    }
  }
  return wrapCanvasTex(c, true);
}

/** Olive ammo can — banding, rivets, stencil. */
function makeMilCrateMap(): THREE.CanvasTexture {
  const size = 512;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#3a4a28';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = `rgba(0,0,0,${0.04 + (i % 5) * 0.012})`;
    ctx.fillRect((i * 47) % size, (i * 31) % size, 3, 3);
  }
  ctx.fillStyle = '#2a341c';
  ctx.fillRect(0, 0, size, 48);
  ctx.fillRect(0, size - 48, size, 48);
  ctx.fillRect(0, 0, 36, size);
  ctx.fillRect(size - 36, 0, 36, size);
  ctx.fillStyle = '#4a5c30';
  ctx.fillRect(44, 56, size - 88, 18);
  ctx.fillRect(44, size - 74, size - 88, 18);
  ctx.strokeStyle = '#1a2010';
  ctx.lineWidth = 10;
  ctx.strokeRect(18, 18, size - 36, size - 36);
  ctx.fillStyle = '#5a6c38';
  const rivets = [28, 28, size - 28, 28, 28, size - 28, size - 28, size - 28, size * 0.5, 24, size * 0.5, size - 24];
  for (let i = 0; i < rivets.length; i += 2) {
    ctx.beginPath();
    ctx.arc(rivets[i]!, rivets[i + 1]!, 6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = '#c8b86a';
  ctx.font = 'bold 72px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('AMMO', size * 0.5, size * 0.46);
  ctx.font = 'bold 28px sans-serif';
  ctx.fillStyle = '#8a9a58';
  ctx.fillText('M2A1', size * 0.5, size * 0.62);
  return wrapCanvasTex(c, true);
}

/** Plank crate with straps and nail heads. */
function makeWoodCrateMap(): THREE.CanvasTexture {
  const size = 512;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#3a2a18';
  ctx.fillRect(0, 0, size, size);
  const planks = ['#8a6a3c', '#7a5c34', '#946e40', '#6e522c', '#826238'];
  const n = 6;
  const pw = size / n;
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = planks[i % planks.length]!;
    ctx.fillRect(i * pw + 2, 0, pw - 4, size);
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect((i + 1) * pw - 3, 0, 3, size);
  }
  ctx.fillStyle = '#2a2c28';
  ctx.fillRect(0, 70, size, 36);
  ctx.fillRect(0, size - 106, size, 36);
  ctx.fillStyle = '#4a4c46';
  ctx.fillRect(0, 78, size, 8);
  ctx.fillRect(0, size - 90, size, 8);
  ctx.fillStyle = '#c4b090';
  for (const y of [88, size - 88]) {
    for (let i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.arc(i * pw + pw * 0.5, y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  return wrapCanvasTex(c, true);
}

/** Herringbone parquet — oak blocks, dark grout. Tiles on a 2-cell period. */
function makeParquetFloorMap(): THREE.CanvasTexture {
  const size = 512;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#2c2218';
  ctx.fillRect(0, 0, size, size);
  const tones = ['#8a5c32', '#7a4e28', '#966438', '#6e4624', '#82562e', '#8e6a3c', '#744c26'];
  const unit = size / 8;
  const grout = 2;
  const plank = (x: number, y: number, w: number, h: number, seed: number) => {
    const tone = tones[Math.abs(seed * 13 + 7) % tones.length]!;
    ctx.fillStyle = tone;
    ctx.fillRect(x + grout, y + grout, w - grout * 2, h - grout * 2);
    ctx.fillStyle = 'rgba(255,220,160,0.07)';
    ctx.fillRect(x + grout, y + grout, w - grout * 2, 2);
    ctx.fillStyle = 'rgba(0,0,0,0.16)';
    ctx.fillRect(x + grout, y + h - grout - 2, w - grout * 2, 2);
    ctx.strokeStyle = 'rgba(40,24,12,0.18)';
    ctx.lineWidth = 1;
    const alongX = w > h;
    const steps = alongX ? 5 : 7;
    for (let i = 1; i < steps; i++) {
      ctx.beginPath();
      if (alongX) {
        const yy = y + grout + ((h - grout * 2) * i) / steps;
        ctx.moveTo(x + grout + 1, yy);
        ctx.lineTo(x + w - grout - 1, yy);
      } else {
        const xx = x + grout + ((w - grout * 2) * i) / steps;
        ctx.moveTo(xx, y + grout + 1);
        ctx.lineTo(xx, y + h - grout - 1);
      }
      ctx.stroke();
    }
  };
  for (let j = -2; j < 10; j++) {
    for (let i = -2; i < 10; i++) {
      const seed = i * 17 + j * 31;
      if (((i + j) & 1) === 0) plank(i * unit, j * unit, unit * 2, unit, seed);
      else plank(i * unit, j * unit, unit, unit * 2, seed);
    }
  }
  return wrapCanvasTex(c, true);
}

/** Painted steel locker doors. */
function makeLockerMap(): THREE.CanvasTexture {
  const size = 512;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#2e322c';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#4a5248';
  ctx.fillRect(16, 14, 230, 484);
  ctx.fillRect(266, 14, 230, 484);
  ctx.strokeStyle = '#1e221c';
  ctx.lineWidth = 8;
  ctx.strokeRect(16, 14, 230, 484);
  ctx.strokeRect(266, 14, 230, 484);
  ctx.fillStyle = '#3a4038';
  ctx.fillRect(28, 26, 206, 200);
  ctx.fillRect(278, 26, 206, 200);
  ctx.fillRect(28, 248, 206, 234);
  ctx.fillRect(278, 248, 206, 234);
  ctx.fillStyle = '#242820';
  for (let i = 0; i < 6; i++) {
    ctx.fillRect(52, 42 + i * 16, 158, 7);
    ctx.fillRect(302, 42 + i * 16, 158, 7);
  }
  ctx.fillStyle = '#b8a060';
  ctx.fillRect(208, 268, 22, 64);
  ctx.fillRect(282, 268, 22, 64);
  ctx.fillStyle = '#8a7848';
  ctx.fillRect(212, 272, 14, 20);
  ctx.fillRect(286, 272, 14, 20);
  return wrapCanvasTex(c, true);
}

export type CrateStyle = 'mil' | 'wood';

export function crateStyleForId(id: string): CrateStyle {
  if (id.includes('stash_s') || id.includes('stash_w1')) return 'wood';
  if (id.includes('tank') || id.includes('stash_w')) return 'mil';
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return (h & 1) === 0 ? 'mil' : 'wood';
}

/** Shared military-compound surface materials (albedo-tinted). */
export function createArenaMaterials() {
  const floorMap = loadAlbedo('/assets/textures/MetalPlates013/albedo.jpg', 14, 10);
  const wallMap = loadAlbedo('/assets/textures/Plaster001/albedo.jpg', 10, 3);
  const metalMap = loadAlbedo('/assets/textures/Metal032/albedo.jpg', 4.5, 4.5);
  const tankMetalMap = loadAlbedo('/assets/textures/Metal032/albedo.jpg', 2.4, 1.6);
  const deskMap = loadAlbedo('/assets/textures/WoodSiding009/albedo.jpg', 3, 1.5);
  const rampMap = loadAlbedo('/assets/textures/MetalPlates006/albedo.jpg', 4, 3);
  const ceilingMap = loadAlbedo('/assets/textures/Metal055A/albedo.jpg', 16, 12);
  const brickMap = loadAlbedo('/assets/textures/Bricks075A/albedo.jpg', 6, 4);
  const pavingMap = loadAlbedo('/assets/textures/PavingStones070/albedo.jpg', 8, 8);
  const stainMap = loadAlbedo('/assets/textures/Rock023/albedo.jpg', 8, 8);
  const groundMap = loadAlbedo('/assets/textures/Ground037/albedo.jpg', 8, 8);

  const floor = new THREE.MeshStandardMaterial({
    map: floorMap,
    color: 0xb8b4a8,
    roughness: 0.78,
    metalness: 0.35,
  });
  const wall = new THREE.MeshStandardMaterial({
    map: wallMap,
    color: 0x8a9278,
    roughness: 0.96,
    metalness: 0,
  });
  const metal = new THREE.MeshStandardMaterial({
    map: metalMap,
    color: 0x9aa0a8,
    roughness: 0.4,
    metalness: 0.85,
  });
  const ventMap = loadAlbedo('/assets/textures/vent/xpanel.png', 1, 1);
  const vent = new THREE.MeshStandardMaterial({
    map: ventMap,
    color: 0x8a928e,
    roughness: 0.6,
    metalness: 0.48,
  });
  const crateMil = new THREE.MeshStandardMaterial({
    map: makeMilCrateMap(),
    color: 0xc4d0a0,
    roughness: 0.52,
    metalness: 0.42,
  });
  const crateWood = new THREE.MeshStandardMaterial({
    map: makeWoodCrateMap(),
    color: 0xe8d0a8,
    roughness: 0.88,
    metalness: 0.04,
  });

  const tankMetal = new THREE.MeshStandardMaterial({
    map: tankMetalMap,
    color: 0xc8d2d8,
    roughness: 0.36,
    metalness: 0.88,
  });

  return {
    floor,
    wall,
    metal,
    vent,
    tankMetal,
    crateMil,
    crateWood,
    crate: crateMil,
    locker: new THREE.MeshStandardMaterial({
      map: makeLockerMap(),
      color: 0xd0ccc0,
      roughness: 0.58,
      metalness: 0.22,
    }),
    desk: new THREE.MeshStandardMaterial({
      map: deskMap,
      color: 0x6a5a48,
      roughness: 0.85,
      metalness: 0.05,
    }),
    ramp: new THREE.MeshStandardMaterial({
      map: rampMap,
      color: 0xa8a090,
      roughness: 0.75,
      metalness: 0.4,
    }),
    hazard: new THREE.MeshStandardMaterial({
      color: 0xc9a020,
      roughness: 0.65,
      metalness: 0.2,
    }),
    hazardBlack: new THREE.MeshStandardMaterial({
      color: 0x1a1a16,
      roughness: 0.72,
      metalness: 0.12,
    }),
    ceiling: new THREE.MeshStandardMaterial({
      map: ceilingMap,
      color: 0x8a9098,
      roughness: 0.55,
      metalness: 0.7,
    }),
    brick: new THREE.MeshStandardMaterial({
      map: brickMap,
      color: 0xb8a898,
      roughness: 0.88,
      metalness: 0.04,
    }),
    paving: new THREE.MeshStandardMaterial({
      map: pavingMap,
      color: 0x9a9a92,
      roughness: 0.82,
      metalness: 0.18,
    }),
    stain: new THREE.MeshStandardMaterial({
      map: stainMap,
      color: 0x6a6860,
      roughness: 0.92,
      metalness: 0.08,
    }),
    ground: new THREE.MeshStandardMaterial({
      map: groundMap,
      color: 0x7a7268,
      roughness: 0.9,
      metalness: 0.06,
    }),
    wallBath: new THREE.MeshStandardMaterial({
      map: makeClinkerTileMap(),
      color: 0xe8e2d4,
      roughness: 0.92,
      metalness: 0,
    }),
    wallSec: tinted(wall, 0x9a8c6e),
    wallKitchen: tinted(wall, 0xb0a078),
    wallLab: tinted(wall, 0x7a8c9a),
    wallIndustrial: tinted(wall, 0x6e747c),
    wallStair: tinted(wall, 0xd4d0c4),
    floorBath: new THREE.MeshStandardMaterial({
      map: makeTealFloorTileMap(),
      color: 0x7aa8a0,
      roughness: 0.62,
      metalness: 0.03,
    }),
    floorSec: tinted(floor, 0xc4b498),
    floorLab: tinted(floor, 0xa8b4c0),
    floorHall: tinted(floor, 0xb0aea4),
    floorParquet: new THREE.MeshStandardMaterial({
      map: makeParquetFloorMap(),
      color: 0xc8a878,
      roughness: 0.74,
      metalness: 0.04,
    }),
    pipe: new THREE.MeshStandardMaterial({
      map: metalMap,
      color: 0x5a6570,
      roughness: 0.32,
      metalness: 0.82,
    }),
    pipeRust: new THREE.MeshStandardMaterial({
      map: metalMap,
      color: 0x8a6a48,
      roughness: 0.48,
      metalness: 0.7,
    }),
    plastic: new THREE.MeshStandardMaterial({
      color: 0x2a2e32,
      roughness: 0.48,
      metalness: 0.18,
    }),
    screen: new THREE.MeshStandardMaterial({
      color: 0x0c1814,
      emissive: 0x1a4a38,
      emissiveIntensity: 0.85,
      roughness: 0.22,
      metalness: 0.1,
    }),
    paper: new THREE.MeshStandardMaterial({
      color: 0xd8d0c0,
      roughness: 0.92,
      metalness: 0,
    }),
    bathDoor: new THREE.MeshStandardMaterial({
      color: 0x4a86a0,
      roughness: 0.52,
      metalness: 0.06,
    }),
    door: new THREE.MeshStandardMaterial({
      color: 0x2a4d34,
      roughness: 0.58,
      metalness: 0.05,
    }),
    chrome: new THREE.MeshStandardMaterial({
      color: 0xf2f6f8,
      roughness: 0.14,
      metalness: 0.95,
      emissive: 0x8a9298,
      emissiveIntensity: 0.22,
    }),
  };
}

export type ArenaMats = ReturnType<typeof createArenaMaterials>;

const TILE_FLOOR = 0.62;
export const TILE_WALL = 0.72;
const TILE_PROP = 0.5;
/** X-panel sheet is 2 squares wide; one square fills the duct height (~1.22 m). */
const TILE_VENT_U = 2.44;
const TILE_VENT_V = 1.22;

const _wallUv = new THREE.Vector3();

/** Vertex UVs from world X/Y or Z/Y so split wall pieces share one brick grid. */
export function applyWorldWallUvs(mesh: THREE.Mesh, along: 'x' | 'z' = 'x'): void {
  const pos = mesh.geometry.attributes.position;
  const uv = mesh.geometry.attributes.uv;
  if (!pos || !uv) return;
  mesh.updateMatrix();
  for (let i = 0; i < pos.count; i++) {
    _wallUv.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrix);
    uv.setXY(i, (along === 'x' ? _wallUv.x : _wallUv.z) / TILE_WALL, _wallUv.y / TILE_WALL);
  }
  uv.needsUpdate = true;
}

/** World-size UV repeats so tiles stay ~0.5–0.8 m regardless of brush AABB. */
export function uvRepeatForBrush(
  size: THREE.Vector3,
  kind: 'floor' | 'wall' | 'ceiling' | 'prop',
): { u: number; v: number } {
  const sx = Math.max(0.05, size.x);
  const sy = Math.max(0.05, size.y);
  const sz = Math.max(0.05, size.z);
  if (kind === 'floor' || kind === 'ceiling') {
    return { u: sx / TILE_FLOOR, v: sz / TILE_FLOOR };
  }
  if (kind === 'wall') {
    const len = Math.max(sx, sz);
    return { u: len / TILE_WALL, v: sy / TILE_WALL };
  }
  const len = Math.max(sx, sz);
  return { u: len / TILE_PROP, v: sy / TILE_PROP };
}

/** World UVs for the 2-wide X-panel vent sheet so the diamonds stay square. */
export function uvRepeatForVent(
  size: THREE.Vector3,
  kind: 'wall' | 'ceiling',
): { u: number; v: number } {
  const sx = Math.max(0.05, size.x);
  const sy = Math.max(0.05, size.y);
  const sz = Math.max(0.05, size.z);
  if (kind === 'ceiling') return { u: sx / TILE_VENT_U, v: sz / TILE_VENT_V };
  const len = Math.max(sx, sz);
  return { u: len / TILE_VENT_U, v: sy / TILE_VENT_V };
}

/** Clone a material and its albedo so this mesh can have its own repeat. */
export function cloneMatWorldUv(
  base: THREE.MeshStandardMaterial,
  repeatU: number,
  repeatV: number,
): THREE.MeshStandardMaterial {
  const mat = base.clone();
  const ru = Math.max(0.35, repeatU);
  const rv = Math.max(0.35, repeatV);
  const cloneMap = (src: THREE.Texture): THREE.Texture => {
    const map = src.clone();
    map.wrapS = THREE.RepeatWrapping;
    map.wrapT = THREE.RepeatWrapping;
    map.repeat.set(ru, rv);
    map.anisotropy = Math.max(map.anisotropy, 8);
    map.needsUpdate = true;
    return map;
  };
  if (base.map) mat.map = cloneMap(base.map);
  if (base.roughnessMap) mat.roughnessMap = cloneMap(base.roughnessMap);
  return mat;
}

export function pickFacilityMaterial(
  mats: ArenaMats,
  id: string,
  surf: keyof ArenaMats,
): THREE.MeshStandardMaterial {
  const base = mats[surf];
  if (surf === 'floor') {
    if (id.includes('bath')) return mats.floorBath;
    if (
      id.includes('foyer') ||
      id.includes('hub') ||
      id.includes('sec') ||
      id.includes('kitchen') ||
      id.includes('locker') ||
      id.includes('stash')
    ) {
      return mats.floorParquet;
    }
    if (id.includes('lab')) return mats.floorLab;
    if (id.includes('catwalk') || id.startsWith('lip_cw') || id.startsWith('landing')) {
      return mats.ramp;
    }
    if (id.includes('stairdn') || id.includes('wing')) {
      return mats.floorHall;
    }
    return mats.floor;
  }
  if (surf === 'wall') {
    if (id.includes('bath') && !id.includes('bath_n')) return mats.wallBath;
    if (id.includes('foyer') || id.includes('bath_n')) return mats.wall;
    if (id.includes('stair')) return mats.wallStair;
    if (id.includes('kitchen')) return mats.wallKitchen;
    if (id.includes('sec') || id.includes('hub') || id.includes('wing')) return mats.wallSec;
    if (id.includes('lab')) return mats.wallLab;
    if (id.includes('tank') || id.includes('bottle') || id.includes('_bot')) return mats.wallIndustrial;
    return mats.wall;
  }
  if (id.startsWith('vent_')) return mats.vent;
  if (id.startsWith('jamb')) return mats.tankMetal;
  if (id.startsWith('cabinet')) return mats.locker;
  if (id.startsWith('crate')) {
    return crateStyleForId(id) === 'wood' ? mats.crateWood : mats.crateMil;
  }
  return base;
}

export function uvKindForSurf(surf: keyof ArenaMats): 'floor' | 'wall' | 'ceiling' | 'prop' {
  if (
    surf === 'floor' ||
    surf === 'floorBath' ||
    surf === 'floorSec' ||
    surf === 'floorLab' ||
    surf === 'floorHall' ||
    surf === 'floorParquet'
  ) {
    return 'floor';
  }
  if (surf === 'paving' || surf === 'stain' || surf === 'ground') return 'floor';
  if (surf === 'ceiling') return 'ceiling';
  if (surf === 'vent') return 'wall';
  if (
    surf === 'wall' ||
    surf === 'wallBath' ||
    surf === 'wallSec' ||
    surf === 'wallKitchen' ||
    surf === 'wallLab' ||
    surf === 'wallIndustrial' ||
    surf === 'wallStair' ||
    surf === 'brick'
  ) {
    return 'wall';
  }
  if (surf === 'crate' || surf === 'crateMil' || surf === 'crateWood') return 'prop';
  return 'prop';
}
